import { ENGINE_VERSION } from './types';
/** Component style keys every theme must fully populate. */
const REQUIRED_COMPONENT_KEYS = [
    'button', 'card', 'input', 'modal', 'dialog', 'bottomNavigation',
    'navbar', 'badge', 'chip', 'avatar', 'toast', 'progress', 'loading',
    'emptyState', 'errorState', 'skeleton',
];
/** Minimum icon keys the engine's core components are assumed to need. */
const REQUIRED_ICON_KEYS = [
    'home', 'back', 'close', 'settings', 'search', 'notification',
];
/** Minimum asset keys required for baseline app chrome. */
const REQUIRED_ASSET_KEYS = ['logo', 'splash'];
/** Minimum illustration keys required for baseline states. */
const REQUIRED_ILLUSTRATION_KEYS = ['emptyState', 'errorState'];
function isNonEmptyString(v) {
    return typeof v === 'string' && v.trim().length > 0;
}
/**
 * Minimal, dependency-free semver-range compatibility check.
 * Supports the ">=x.y.z <a.b.c" style range produced by our own manifests.
 * Falls back to a strict equality check for anything it can't parse,
 * which fails closed (safer than assuming compatibility).
 */
function isEngineCompatible(range, engineVersion) {
    const toParts = (v) => v.trim().split('.').map((n) => parseInt(n, 10));
    const compare = (a, b) => {
        for (let i = 0; i < 3; i++) {
            if ((a[i] ?? 0) !== (b[i] ?? 0))
                return (a[i] ?? 0) - (b[i] ?? 0);
        }
        return 0;
    };
    const engine = toParts(engineVersion);
    const clauses = range.split(' ').filter(Boolean);
    if (clauses.length === 0)
        return false;
    return clauses.every((clause) => {
        const match = clause.match(/^(>=|<=|>|<|=)?(\d+\.\d+\.\d+)$/);
        if (!match)
            return false;
        const op = match[1] ?? '=';
        const version = match[2];
        const target = toParts(version);
        const cmp = compare(engine, target);
        switch (op) {
            case '>=': return cmp >= 0;
            case '<=': return cmp <= 0;
            case '>': return cmp > 0;
            case '<': return cmp < 0;
            default: return cmp === 0;
        }
    });
}
/**
 * ThemeValidator
 * ------------------------
 * Verifies a theme object fully and correctly satisfies the ITheme
 * contract before the engine allows it to become active. Validation is
 * intentionally strict and additive-safe: it never mutates the theme,
 * it only reports issues for the loader to act on.
 */
export class ThemeValidator {
    validate(theme) {
        const issues = [];
        if (!theme) {
            return { valid: false, issues: [{ path: '(root)', message: 'Theme is null or undefined.' }] };
        }
        this.validateManifest(theme, issues);
        this.validateTokens(theme, issues);
        this.validateComponents(theme, issues);
        this.validateIcons(theme, issues);
        this.validateAssets(theme, issues);
        this.validateIllustrations(theme, issues);
        this.validateAccessibility(theme, issues);
        this.validateLocalization(theme, issues);
        this.validateLayout(theme, issues);
        return { valid: issues.length === 0, issues };
    }
    validateManifest(theme, issues) {
        const m = theme.manifest;
        if (!m) {
            issues.push({ path: 'manifest', message: 'Missing manifest.' });
            return;
        }
        const requiredStringFields = [
            'id', 'name', 'version', 'author', 'description',
            'previewImage', 'coverImage', 'category', 'engineCompatibility',
        ];
        requiredStringFields.forEach((field) => {
            if (!isNonEmptyString(m[field])) {
                issues.push({ path: `manifest.${field}`, message: `Required manifest field "${field}" is missing or empty.` });
            }
        });
        if (!Array.isArray(m.tags)) {
            issues.push({ path: 'manifest.tags', message: 'manifest.tags must be an array.' });
        }
        if (!Array.isArray(m.supportedLanguages) || m.supportedLanguages.length === 0) {
            issues.push({ path: 'manifest.supportedLanguages', message: 'manifest.supportedLanguages must be a non-empty array.' });
        }
        if (typeof m.premium !== 'boolean') {
            issues.push({ path: 'manifest.premium', message: 'manifest.premium must be a boolean.' });
        }
        if (typeof m.rtlSupport !== 'boolean') {
            issues.push({ path: 'manifest.rtlSupport', message: 'manifest.rtlSupport must be a boolean.' });
        }
        if (isNonEmptyString(m.engineCompatibility) && !isEngineCompatible(m.engineCompatibility, ENGINE_VERSION)) {
            issues.push({
                path: 'manifest.engineCompatibility',
                message: `Theme declares compatibility "${m.engineCompatibility}", incompatible with engine v${ENGINE_VERSION}.`,
            });
        }
    }
    validateTokens(theme, issues) {
        const t = theme.tokens;
        if (!t) {
            issues.push({ path: 'tokens', message: 'Missing design tokens.' });
            return;
        }
        const requiredGroups = [
            'colors', 'typography', 'spacing', 'radius', 'shadows',
            'opacity', 'zIndex', 'breakpoints', 'motion',
        ];
        requiredGroups.forEach((group) => {
            if (!t[group] || typeof t[group] !== 'object') {
                issues.push({ path: `tokens.${group}`, message: `Required token group "${group}" is missing.` });
            }
        });
    }
    validateComponents(theme, issues) {
        const c = theme.components;
        if (!c) {
            issues.push({ path: 'components', message: 'Missing component style layer.' });
            return;
        }
        REQUIRED_COMPONENT_KEYS.forEach((key) => {
            if (!c[key]) {
                issues.push({ path: `components.${key}`, message: `Missing required component style contract "${key}".` });
            }
        });
    }
    validateIcons(theme, issues) {
        const icons = theme.icons;
        if (!icons) {
            issues.push({ path: 'icons', message: 'Missing icon set.' });
            return;
        }
        REQUIRED_ICON_KEYS.forEach((key) => {
            if (!isNonEmptyString(icons[key])) {
                issues.push({ path: `icons.${key}`, message: `Missing required icon "${key}".` });
            }
        });
    }
    validateAssets(theme, issues) {
        const assets = theme.assets;
        if (!assets) {
            issues.push({ path: 'assets', message: 'Missing asset set.' });
            return;
        }
        REQUIRED_ASSET_KEYS.forEach((key) => {
            if (!isNonEmptyString(assets[key])) {
                issues.push({ path: `assets.${key}`, message: `Missing required asset "${key}".` });
            }
        });
    }
    validateIllustrations(theme, issues) {
        const illustrations = theme.illustrations;
        if (!illustrations) {
            issues.push({ path: 'illustrations', message: 'Missing illustration set.' });
            return;
        }
        REQUIRED_ILLUSTRATION_KEYS.forEach((key) => {
            if (!isNonEmptyString(illustrations[key])) {
                issues.push({ path: `illustrations.${key}`, message: `Missing required illustration "${key}".` });
            }
        });
    }
    validateAccessibility(theme, issues) {
        const a = theme.accessibility;
        if (!a) {
            issues.push({ path: 'accessibility', message: 'Missing accessibility declaration.' });
            return;
        }
        if (typeof a.minContrastRatio !== 'number' || a.minContrastRatio < 4.5) {
            issues.push({ path: 'accessibility.minContrastRatio', message: 'minContrastRatio must be a number >= 4.5.' });
        }
        if (!a.focusRing || !isNonEmptyString(a.focusRing.color)) {
            issues.push({ path: 'accessibility.focusRing', message: 'focusRing must be defined with a color reference.' });
        }
    }
    validateLocalization(theme, issues) {
        const l = theme.localization;
        if (!l) {
            issues.push({ path: 'localization', message: 'Missing localization hooks.' });
            return;
        }
        if (!Array.isArray(l.supportedDirections) || l.supportedDirections.length === 0) {
            issues.push({ path: 'localization.supportedDirections', message: 'supportedDirections must be a non-empty array.' });
        }
        if (theme.manifest?.rtlSupport && !l.supportedDirections?.includes('rtl')) {
            issues.push({
                path: 'localization.supportedDirections',
                message: 'manifest.rtlSupport is true but localization.supportedDirections does not include "rtl".',
            });
        }
    }
    validateLayout(theme, issues) {
        const layout = theme.layout;
        if (!layout) {
            issues.push({ path: 'layout', message: 'Missing layout definitions.' });
            return;
        }
        if (!layout.containerPadding || !layout.maxContentWidth) {
            issues.push({ path: 'layout', message: 'layout.containerPadding and layout.maxContentWidth are required.' });
        }
        // Note: soundPack is intentionally NOT validated — it is an optional,
        // forward-compatible placeholder (see sound.contract.ts).
    }
}
export const themeValidator = new ThemeValidator();
